import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  AngularFirestore,
  AngularFirestoreCollection,
} from '@angular/fire/compat/firestore';
import { FirestoreError, Timestamp } from '@angular/fire/firestore';
import axios from 'axios';
import { catchError, map, Observable, take } from 'rxjs';
import { environment } from '../../environments/environment';
import { AppExceptionModel } from '../models/app-exception.model';
import { Award } from '../models/award.school.model';
import { UtilsService } from './utils.service';

@Injectable({
  providedIn: 'root',
})
export class SchoolAwardService {
  http = inject(HttpClient);
  afs: AngularFirestore = inject(AngularFirestore);
  utilsService = inject(UtilsService);

  constructor() {}

  getAwards({
    groupId,
    academicYear,
    gradeId,
    studentId,
    limit,
    lastRecord,
  }: {
    groupId: string;
    academicYear: string;
    gradeId?: string;
    studentId?: string;
    limit?: number;
    tags?: string[];
    lastRecord?: Timestamp;
  }): Observable<Award[]> {
    let query: AngularFirestoreCollection<Award> = this.afs
      .collection('groups')
      .doc(groupId)
      .collection('awards', (ref) => {
        var reference = ref.where('academicYear', '==', academicYear);

        if (gradeId) {
          reference = reference.where('gradeId', '==', gradeId);
        }

        if (studentId) {
          reference = reference.where('studentId', '==', studentId);
        }

        reference = reference.orderBy('modified', 'desc');

        if (limit) reference = reference.limit(limit ?? 10);

        if (lastRecord) reference = reference.startAfter(lastRecord);

        return reference;
      });

    return query.get().pipe(
      take(1),
      map((changes) => {
        return changes.docs.map((a) => {
          var data: Award = a.data() as Award;
          data.id = a.id;
          return data;
        });
      }),
      catchError((error: FirestoreError) => {
        // console.log(`GradeAwardService :: getGradeAwardsByGroup: Error: ${error}`);
        return [];
      }),
    );
  }

  getAllAwardsByStudentId({
    studentId,
    limit,
    lastRecord,
  }: {
    studentId: string;
    limit?: number;
    lastRecord?: Timestamp;
  }): Observable<Award[]> {
    const query = this.afs.collectionGroup<Award>('awards', (ref) => {
      let reference = ref.where('studentId', '==', studentId);
      reference = reference.orderBy('modified', 'desc');

      if (limit) reference = reference.limit(limit ?? 10);
      if (lastRecord) reference = reference.startAfter(lastRecord);

      return reference;
    });

    return query.get().pipe(
      take(1),
      map((changes) => {
        return changes.docs.map((a) => {
          const data = a.data() as Award;
          data.id = a.id;
          if (!data.groupId && a.ref.parent?.parent?.id) {
            data.groupId = a.ref.parent.parent.id;
          }
          return data;
        });
      }),
      catchError((error: FirestoreError) => {
        console.error('SchoolAwardService :: getAllAwardsByStudentId Error:', error);
        return [];
      }),
    );
  }
  catch(error: any) {
    // console.log(
    //   `GradeAwardService :: getGradeAwardsByGroup: Error: ${error?.toString()}`
    // );
    const appException: AppExceptionModel = {
      code: 'ERR_GET_ASSIGNMENTS',
      message: 'Unable to fetch Awards at this time. Please try again later',
      title: 'Error Fetching Awards',
    };
    throw appException;
  }

  getAwardById(groupId: string, id: string): Observable<Award> {
    const doc = this.afs
      .collection('groups')
      .doc(groupId)
      .collection('awards')
      .doc(id)
      .get();
    return doc.pipe(
      take(1),
      map((doc) => {
        return { ...doc.data(), id: id } as Award;
      }),
    );
  }

  async create(groupId: string, award: Award): Promise<string> {
    // console.log('AwardService:: create:', JSON.stringify(award, null, 2));
    const doc = await this.afs
      .collection('groups')
      .doc(groupId)
      .collection('awards')
      .add(award);
    return doc.id;
  }

  async update(groupId: string, id: string, award: Award): Promise<void> {
    // console.log(
    //   `AwardService:: update: ${id}: `,
    //   JSON.stringify(award, null, 2)
    // );
    return this.afs
      .collection('groups')
      .doc(groupId)
      .collection('awards')
      .doc(id)
      .update(award);
  }

  async getAwardDocument(url: string) {
    // console.log('AwardService:: getNotes: ', url);
    // const config: AxiosRequestConfig = {
    //   method: 'GET',
    //   headers: {
    //     'Content-Type': 'application/json',
    //   },
    // };

    return axios(url).then((response) => response.data);
  }

  async getUploadUrl(groupId: string, awardId: string): Promise<any> {
    return this.utilsService
      .setAxiosHeaders(true)
      .then((headers) => {
        return axios.get<any>(environment['AWS_SCHOOL_AWARDS_SIGNEDURL']!, {
          params: { groupId: groupId, awardId: awardId },
          headers: headers,
        });
      })
      .then((response) => {
        return {
          method: 'PUT',
          url: response.data.url,
          headers: {
            'Content-Type': `application/pdf`,
          },
        };
      });
  }

  async delete(groupId: string, id: string): Promise<void> {
    return this.afs
      .collection('groups')
      .doc(groupId)
      .collection<Award>('awards')
      .doc(id)
      .delete();
  }
}
